import type { ReactElement } from "react";
import { ChevronRight, Flag } from "lucide-react";
import type { ProjectHealth } from "@playbookai/types";
import { StatusDot, type StatusTone } from "../../atoms/StatusDot/index.js";
import type { WidgetBodyProps } from "../types.js";
import { useWidgetActions } from "../widget-actions.js";
import type { ProjectPulseView } from "./types.js";

/** Project health → the StatusDot tone that paints today's dot colour. */
const HEALTH_TONE: Record<ProjectHealth, StatusTone> = {
  green: "healthy",
  amber: "watch",
  red: "critical",
  blue: "info",
  grey: "neutral",
};

/**
 * Project Pulse — dashboard data widget. Active improvement projects worth a
 * glance: health dot + name + KPI line + next-action pulse. Data-blind: the host
 * resolves the project list. Rows are display-only unless the host supplies
 * `useWidgetActions().openProject` (PB-180); when present each row becomes a
 * keyboard-accessible button. When absent the markup is unchanged — the
 * data-blind body never navigates itself.
 */
export function ProjectPulseWidget({
  data,
}: WidgetBodyProps<ProjectPulseView>): ReactElement {
  const { projects } = data;
  const actions = useWidgetActions();
  const openProject = actions?.openProject;
  const openImprovementWork = actions?.openImprovementWork;
  const needsAttention = projects.filter(
    (p) => p.health === "red" || p.health === "amber",
  ).length;

  if (projects.length === 0) {
    return (
      <p className="text-dense-1 text-pb-text-muted italic">
        No active improvement projects.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <span className="flex items-center gap-1.5 text-dense-1 text-pb-text-muted">
        <Flag className="w-3 h-3 text-pb-cyan-text" />
        {needsAttention > 0
          ? `${needsAttention} active improvement project${needsAttention === 1 ? "" : "s"} ${needsAttention === 1 ? "needs" : "need"} attention.`
          : `${projects.length} active improvement project${projects.length === 1 ? "" : "s"}.`}
      </span>
      <ul className="space-y-1">
        {projects.map((p) => {
          const inner = (
            <>
              <StatusDot tone={HEALTH_TONE[p.health]} className="mt-1" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-pb-text leading-snug truncate">
                  {p.name}
                </span>
                <span className="block text-xs text-pb-text-secondary leading-snug truncate">
                  {p.kpiLine}
                </span>
                <span className="block text-xs text-pb-text-muted leading-snug truncate">
                  {p.pulse}
                </span>
              </span>
            </>
          );
          // No host action → today's exact display-only markup (VR/a11y stable).
          if (!openProject) {
            return (
              <li key={p.id} className="flex items-start gap-2.5 py-1.5">
                {inner}
              </li>
            );
          }
          return (
            <li key={p.id}>
              {/* eslint-disable-next-line ds/no-hand-built -- kept: a clickable row with its own inset and a pulled-out hover; ListItem has no inline padding and its own row layout, which AP-07's selection fill does not cover. */}
              <button
                type="button"
                onClick={() => openProject(p.id)}
                className="flex w-full items-start gap-2.5 py-1.5 text-start rounded-md transition-colors hover:pb-overlay-paint focus-ring"
              >
                {inner}
              </button>
            </li>
          );
        })}
      </ul>
      {openImprovementWork ? (
        // eslint-disable-next-line ds/no-hand-built -- kept: a 14px secondary-ink text action with a chevron; Button link is underlined at inline size, and no approved look row lists the change.
        <button
          type="button"
          onClick={() => openImprovementWork()}
          className="inline-flex items-center self-start text-sm font-medium text-pb-text-secondary transition-colors hover:text-pb-text focus-ring"
        >
          View all projects
          <ChevronRight className="ms-1 h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
